import { useEffect, useState, useRef, useCallback } from "react";
import { ChevronDown, CornerUpLeft } from "lucide-react";
import {
  META,
  SECTIONS,
  SOURCES_SECTION,
  CONTENT,
  SOURCES,
} from "@/data/article";

const ALL = [...SECTIONS, SOURCES_SECTION];
const IDS = ALL.map((s) => s.id);
const HEADER_OFFSET = 84;

const prefersReduced = () =>
  typeof window !== "undefined" &&
  window.matchMedia("(prefers-reduced-motion: reduce)").matches;

/* Simple, left-aligned contents. Shared by the desktop sidebar and the
   inline mobile block. No boxes, no animation — just links. */
function Contents({ active, onNavigate, idPrefix }) {
  return (
    <nav aria-label="Contents">
      <p className="contents-title">Contents</p>
      <ol className="contents-list">
        {ALL.map((s) => (
          <li key={s.id}>
            <a
              href={`#${s.id}`}
              aria-current={active === s.id ? "true" : undefined}
              data-testid={`${idPrefix}-link-${s.id}`}
              onClick={(e) => {
                e.preventDefault();
                onNavigate(s.id);
              }}
            >
              {s.nav}
            </a>
          </li>
        ))}
      </ol>
    </nav>
  );
}

export default function ReadingPage() {
  const [active, setActive] = useState(IDS[0]);
  const [sourcesOpen, setSourcesOpen] = useState(false);
  const rafRef = useRef(0);
  const mobileNavRef = useRef(null);

  useEffect(() => {
    document.title = `${META.title} — ${META.kicker}`;
    document.body.classList.add("reading-body");
    return () => document.body.classList.remove("reading-body");
  }, []);

  // Passive scroll → update ONLY the active section (never the fragment).
  useEffect(() => {
    const compute = () => {
      const marker = 140;
      let current = IDS[0];
      for (const id of IDS) {
        const el = document.getElementById(id);
        if (!el) continue;
        if (el.getBoundingClientRect().top - marker <= 0) current = id;
      }
      // Final-section highlight when parked near the bottom (generous slack).
      if (
        window.innerHeight + window.scrollY >=
        document.documentElement.scrollHeight - 48
      ) {
        current = IDS[IDS.length - 1];
      }
      setActive(current);
    };
    const onScroll = () => {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = requestAnimationFrame(compute);
    };
    compute();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
      cancelAnimationFrame(rafRef.current);
    };
  }, []);

  // Scroll a target flush under the sticky header, focusing without a 2nd scroll.
  const scrollToId = useCallback((id, smooth) => {
    const el = document.getElementById(id);
    if (!el) return;
    const y = el.getBoundingClientRect().top + window.scrollY - HEADER_OFFSET;
    el.focus({ preventScroll: true });
    window.scrollTo({ top: Math.max(0, y), behavior: smooth ? "smooth" : "auto" });
  }, []);

  // Resolve a target. For source targets, open Sources imperatively FIRST so
  // the <details> is expanded and laid out before we measure and scroll.
  const goto = useCallback(
    (id, { updateHash = false, smooth = true } = {}) => {
      if (id === "sources" || id.startsWith("ref-")) {
        const d = document.getElementById("sources");
        if (d) d.open = true;
        setSourcesOpen(true);
      }
      if (updateHash) window.history.pushState(null, "", `#${id}`);
      requestAnimationFrame(() => scrollToId(id, smooth && !prefersReduced()));
    },
    [scrollToId],
  );

  const onNavigate = useCallback(
    (id) => {
      setActive(id);
      goto(id, { updateHash: true });
    },
    [goto],
  );

  // Same as onNavigate, but collapse the pinned mobile Contents bar after a jump.
  const onMobileNavigate = useCallback(
    (id) => {
      onNavigate(id);
      if (mobileNavRef.current) mobileNavRef.current.open = false;
    },
    [onNavigate],
  );

  // Citation clicks: open Sources before resolving the destination.
  const onArticleClick = useCallback(
    (e) => {
      const a =
        e.target.closest &&
        e.target.closest('a[href^="#ref-"], a[href^="#cite-"]');
      if (!a) return;
      e.preventDefault();
      goto(a.getAttribute("href").slice(1), { updateHash: true });
    },
    [goto],
  );

  // Direct loads and Back/Forward: resolve hash (opens Sources for source targets).
  useEffect(() => {
    const resolve = () => {
      const hash = window.location.hash;
      if (!hash) return;
      goto(hash.slice(1), { smooth: false });
    };
    resolve();
    window.addEventListener("popstate", resolve);
    window.addEventListener("hashchange", resolve);
    return () => {
      window.removeEventListener("popstate", resolve);
      window.removeEventListener("hashchange", resolve);
    };
  }, [goto]);

  // Always print the complete sources, even when collapsed onscreen.
  useEffect(() => {
    const open = () => setSourcesOpen(true);
    window.addEventListener("beforeprint", open);
    return () => window.removeEventListener("beforeprint", open);
  }, []);

  return (
    <>
      <a className="skip-link" href="#main" data-testid="skip-link">
        Skip to article
      </a>

      <header className="masthead">
        <a className="masthead-logo" href="#origin" aria-label={`${META.kicker} — home`}>
          <img src="/mithara-logo.png" alt="Mithara Institute" />
        </a>
      </header>

      <div className="shell">
        <aside className="contents-desktop">
          <Contents active={active} onNavigate={onNavigate} idPrefix="contents" />
        </aside>

        <main id="main" tabIndex={-1} className="main-col">
          <details className="contents-mobile" ref={mobileNavRef} data-testid="mobile-contents">
            <summary data-testid="mobile-contents-summary">
              Contents
              <ChevronDown className="contents-mobile-chevron" size={18} strokeWidth={1.5} />
            </summary>
            <div className="contents-mobile-panel">
              <ol className="contents-list">
                {ALL.map((s) => (
                  <li key={s.id}>
                    <a
                      href={`#${s.id}`}
                      aria-current={active === s.id ? "true" : undefined}
                      data-testid={`mobile-contents-link-${s.id}`}
                      onClick={(e) => {
                        e.preventDefault();
                        onMobileNavigate(s.id);
                      }}
                    >
                      {s.nav}
                    </a>
                  </li>
                ))}
              </ol>
            </div>
          </details>

          <article className="article" onClick={onArticleClick}>
            <div className="article-masthead">
              <h1
                className="article-title"
                id="origin"
                tabIndex={-1}
                style={{ scrollMarginTop: `${HEADER_OFFSET}px` }}
              >
                {META.title}
              </h1>
              <p className="article-byline">{META.byline}</p>
            </div>

            <section className="section" aria-labelledby="origin-body">
              <h2 id="origin-body" style={srOnly}>
                Opening
              </h2>
              {CONTENT.origin}
            </section>

            {SECTIONS.slice(1).map((s) => (
              <section
                key={s.id}
                id={s.id}
                className="section"
                tabIndex={-1}
                aria-labelledby={`${s.id}-title`}
              >
                <h2 id={`${s.id}-title`} className="section-title">
                  {s.title}
                </h2>
                {CONTENT[s.id]}
              </section>
            ))}

            <details
              id="sources"
              className="sources"
              open={sourcesOpen}
              onToggle={(e) => setSourcesOpen(e.currentTarget.open)}
              data-testid="sources-section"
            >
              <summary data-testid="sources-summary">
                <span>
                  <span className="sources-title">{SOURCES_SECTION.title}</span>
                </span>
                <ChevronDown className="sources-chevron" size={22} strokeWidth={1.5} />
              </summary>
              <ol className="sources-list">
                {SOURCES.map((src) => (
                  <li key={src.id} id={src.id} tabIndex={-1}>
                    {src.text}
                    <a
                      className="source-return"
                      href={`#${src.cite}`}
                      data-testid={`source-return-${src.id}`}
                    >
                      <CornerUpLeft size={13} strokeWidth={1.5} />
                      Return to citation
                    </a>
                  </li>
                ))}
              </ol>
            </details>
          </article>
        </main>
      </div>
    </>
  );
}

const srOnly = {
  position: "absolute",
  width: 1,
  height: 1,
  padding: 0,
  margin: -1,
  overflow: "hidden",
  clip: "rect(0 0 0 0)",
  whiteSpace: "nowrap",
  border: 0,
};
